import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  TextField,
  Box,
  Paper,
  Button,
  Stack,
  MenuItem,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { getSuiteById, updateSuite } from "../../features/slices/Suite";
import Title from "../../components/Title";
import { AppDispatch } from "../../store";
import { useDispatch } from "react-redux";
import { useNotification } from "../../context/NotificationContext";
import { useLoader } from "../../context/LoaderContext";
import { JsonEditor } from "json-edit-react";
import { maskSecrets } from "../../utils/secrets";
import { debounce } from "lodash";
import CasesTable from "./components/CasesTable";
import { SuiteTagEditor } from "./components/create_suite/SuiteTagEditor";

const STATUS_OPTIONS = ["INIT", "ABORTED"];

const SectionCard: React.FC<{
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, children }) => (
  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
    <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
      {title}
      {subtitle && (
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ ml: 1, fontWeight: 400 }}
        >
          {subtitle}
        </Typography>
      )}
    </Typography>
    <Box sx={{ mt: 1.5 }}>{children}</Box>
  </Paper>
);

const SuiteEdit = () => {
  const { suitId } = useParams();
  const [suite, setSuite] = useState({ title: "", meta_data: {}, status: "" });
  const { notify } = useNotification();
  const { showLoader, hideLoader } = useLoader();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const fetchSuite = useCallback(
    debounce(async () => {
      showLoader();
      try {
        const result = await dispatch(getSuiteById(Number(suitId))).unwrap();
        if (result) setSuite(result);
        else notify("Failed to fetch suite data", "error");
      } catch (error) {
        console.error("Failed to fetch suite", error);
        notify("Failed to fetch suite", "error");
      } finally {
        hideLoader();
      }
    }, 300),
    [dispatch, suitId, showLoader, hideLoader, notify]
  );

  useEffect(() => {
    fetchSuite();
  }, [suitId]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setSuite((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    showLoader();
    try {
      const result = await dispatch(
        updateSuite({
          title: suite.title,
          meta_data: suite.meta_data,
          status: suite.status,
          id: Number(suitId),
        })
      ).unwrap();
      if (result) {
        notify("Suite updated successfully", "success");
        navigate("/suites", { replace: true });
      } else {
        notify("Failed to update suite", "error");
      }
    } catch (error) {
      console.error("Failed to update suite", error);
      notify("Failed to update suite", "error");
    } finally {
      hideLoader();
    }
  };

  const invalid = suite.title === "" || suite.status === "";

  return (
    <Box sx={{ width: "100%", maxWidth: 980, mx: "auto", pb: 6 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Title>Edit Suite {suitId}</Title>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/suites")}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveOutlinedIcon />}
            onClick={handleSubmit}
            disabled={invalid}
          >
            Update suite
          </Button>
        </Stack>
      </Stack>

      <SectionCard title="Details">
        <Stack spacing={2}>
          <TextField
            required
            fullWidth
            id="title"
            label="Title"
            name="title"
            value={suite.title}
            onChange={handleChange}
            autoFocus
          />
          <TextField
            select
            fullWidth
            name="status"
            label="Status"
            id="status"
            value={suite.status || ""}
            onChange={handleChange}
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </SectionCard>

      <SectionCard title="Metadata" subtitle="(read-only · secrets masked)">
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.level1",
          }}
        >
          <JsonEditor
            minWidth={"100%"}
            data={maskSecrets(suite.meta_data)}
            collapse={1}
            restrictAdd
            restrictDelete
            restrictEdit
          />
        </Box>
      </SectionCard>

      <SectionCard title="Tags" subtitle="(optional)">
        <SuiteTagEditor suiteId={Number(suitId)} />
      </SectionCard>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", p: 2.5, pb: 1 }}>
          Suite Cases
        </Typography>
        <CasesTable suitId={Number(suitId)} />
      </Paper>
    </Box>
  );
};

export default SuiteEdit;
